import { useEffect, useState } from "react";
import { endpoints, fmtUSD } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import StateBadge from "@/components/StateBadge";
import { toast } from "sonner";
import { AlertTriangle, Check, ChevronRight, FileSignature } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const STEPS = [
  { key: "buyer", label: "Buyer" },
  { key: "commercial", label: "Commercial terms" },
  { key: "legal", label: "Legal & RCMP" },
  { key: "review", label: "Review & Publish" },
];

export default function AuthorizationDesigner() {
  const [step, setStep] = useState(0);
  const [opps, setOpps] = useState([]);
  const [partners, setPartners] = useState([]);
  const [savedId, setSavedId] = useState(null);
  const [validation, setValidation] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [publishedAt, setPublishedAt] = useState(null);

  const [spec, setSpec] = useState({
    opportunity_id: "",
    buyer: { aws_account_id: "", company_name: "", validated: false },
    reseller: "",
    mode: "agent",
    pricing_model: "flat_annual",
    total_contract_value_usd: 250000,
    currency: "USD",
    duration_months: 36,
    eula_type: "standard",
    eula_notes: "",
    rcmp_percent: 15,
    status: "draft",
  });

  useEffect(() => {
    endpoints.opportunities().then((r) => setOpps(r.opportunities));
    endpoints.partners().then((r) => setPartners(r.partners));
  }, []);

  const applyOpp = (id) => {
    const o = opps.find((x) => x.id === id);
    if (!o) return;
    setSpec((s) => ({
      ...s,
      opportunity_id: o.id,
      buyer: {
        aws_account_id: o.aws_account_id,
        company_name: o.account_name,
        primary_contact: o.primary_contact,
        validated: true,
      },
      total_contract_value_usd: o.amount_usd,
      duration_months: o.term_months,
      reseller: o.reseller_preference,
    }));
  };

  const saveDraft = async () => {
    const created = await endpoints.createAuthorization(spec);
    setSavedId(created.id);
    toast.success("Draft saved", { description: created.id });
    return created;
  };

  const validate = async () => {
    let id = savedId;
    if (!id) id = (await saveDraft()).id;
    const res = await endpoints.validateAuthorization(id);
    setValidation(res);
    if (res.validation_issues.length === 0) {
      toast.success("Validation passed — safe to publish");
    } else {
      toast.error(`${res.validation_issues.length} issue(s) found`);
    }
  };

  const publish = async () => {
    if (confirm.trim() !== "PUBLISH") return;
    try {
      const res = await endpoints.publishAuthorization(savedId, confirm);
      setPublishedAt(`${res.published_at} · ${res.submission_mode === "live" ? "LIVE change set " + res.change_set_id : "SIMULATED " + res.ammp_authorization_id}`);
      setPublishOpen(false);
      setConfirm("");
      toast.success(res.submission_mode === "live" ? "Submitted to AWS Marketplace Catalog" : "Authorization published to AMMP (SIMULATED)",
        { description: res.ammp_authorization_id });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    }
  };

  return (
    <div className="max-w-[1200px] px-12 py-14" data-testid="page-designer">
      <div className="tx-overline mb-2">Module M1</div>
      <h1 className="font-heading font-black text-4xl mb-2">Authorization Designer</h1>
      <p className="text-zinc-600 text-[14px] max-w-2xl mb-8">
        Design the AWS ISV Selling Authorization <em>before</em> the irreversible
        publish gate. Every parameter you lock here becomes immutable in AMMP.
      </p>

      {/* Stepper */}
      <div className="flex items-center gap-0 mb-8 border border-zinc-200">
        {STEPS.map((s, i) => (
          <button
            key={s.key}
            onClick={() => setStep(i)}
            data-testid={`step-${s.key}`}
            className={`flex-1 h-11 px-4 flex items-center gap-2 text-[12px] transition-colors border-r border-zinc-200 last:border-r-0 ${
              i === step
                ? "bg-zinc-50 text-zinc-900"
                : i < step
                  ? "bg-white text-zinc-600 hover:text-zinc-900"
                  : "bg-white text-zinc-400"
            }`}
          >
            <span
              className={`w-5 h-5 rounded-sm border flex items-center justify-center font-mono text-[10px] ${
                i < step
                  ? "border-emerald-500/40 text-emerald-600"
                  : i === step
                    ? "border-orange-500/60 text-orange-600"
                    : "border-zinc-300 text-zinc-400"
              }`}
            >
              {i < step ? <Check className="w-3 h-3" /> : i + 1}
            </span>
            <span className="tx-overline">{s.label}</span>
            {i < STEPS.length - 1 && (
              <ChevronRight className="w-3.5 h-3.5 text-zinc-300 ml-auto" />
            )}
          </button>
        ))}
      </div>

      <div className="border border-zinc-200 bg-zinc-50 p-8">
        {step === 0 && (
          <div className="space-y-5">
            <div>
              <Label className="tx-overline">Attach opportunity (Salesforce)</Label>
              <Select onValueChange={applyOpp} value={spec.opportunity_id}>
                <SelectTrigger data-testid="opportunity-select" className="bg-white border-zinc-200 mt-2 rounded-sm">
                  <SelectValue placeholder="Pick an opportunity to auto-fill…" />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200">
                  {opps.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      <span className="font-mono text-[11px] text-zinc-500 mr-2">{o.id}</span>
                      {o.account_name} · {fmtUSD(o.amount_usd)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <Label className="tx-overline">Buyer AWS Account ID</Label>
                <Input
                  data-testid="buyer-aws-id"
                  value={spec.buyer.aws_account_id}
                  onChange={(e) =>
                    setSpec({ ...spec, buyer: { ...spec.buyer, aws_account_id: e.target.value } })
                  }
                  className="mt-2 bg-white border-zinc-200 font-mono rounded-sm"
                  placeholder="123456789012"
                />
              </div>
              <div>
                <Label className="tx-overline">Buyer company</Label>
                <Input
                  value={spec.buyer.company_name}
                  onChange={(e) =>
                    setSpec({ ...spec, buyer: { ...spec.buyer, company_name: e.target.value } })
                  }
                  className="mt-2 bg-white border-zinc-200 rounded-sm"
                />
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="grid grid-cols-2 gap-5">
            <div>
              <Label className="tx-overline">Reseller (VAR)</Label>
              <Select value={spec.reseller} onValueChange={(v) => setSpec({ ...spec, reseller: v })}>
                <SelectTrigger data-testid="reseller-select" className="bg-white border-zinc-200 mt-2 rounded-sm">
                  <SelectValue placeholder="Choose reseller…" />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200">
                  {partners.map((p) => (
                    <SelectItem key={p.partner_name} value={p.partner_name}>
                      {p.partner_name} · {p.cp_status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="tx-overline">Operating mode</Label>
              <Select value={spec.mode} onValueChange={(v) => setSpec({ ...spec, mode: v })}>
                <SelectTrigger className="bg-white border-zinc-200 mt-2 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200">
                  <SelectItem value="agent">Agent (execute on VAR's AMMP)</SelectItem>
                  <SelectItem value="principal">Principal (we are the VAR)</SelectItem>
                  <SelectItem value="broker">Broker (route the deal)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="tx-overline">Pricing model</Label>
              <Select
                value={spec.pricing_model}
                onValueChange={(v) => setSpec({ ...spec, pricing_model: v })}
              >
                <SelectTrigger className="bg-white border-zinc-200 mt-2 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200">
                  <SelectItem value="flat_annual">Flat annual</SelectItem>
                  <SelectItem value="usage_based">Usage-based</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="tx-overline">Currency</Label>
              <Select
                value={spec.currency}
                onValueChange={(v) => setSpec({ ...spec, currency: v })}
              >
                <SelectTrigger className="bg-white border-zinc-200 mt-2 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200">
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="tx-overline">Total contract value</Label>
              <Input
                type="number"
                data-testid="tcv-input"
                value={spec.total_contract_value_usd}
                onChange={(e) =>
                  setSpec({ ...spec, total_contract_value_usd: Number(e.target.value) })
                }
                className="mt-2 bg-white border-zinc-200 font-mono rounded-sm"
              />
            </div>
            <div>
              <Label className="tx-overline">Duration (months)</Label>
              <Select
                value={String(spec.duration_months)}
                onValueChange={(v) => setSpec({ ...spec, duration_months: Number(v) })}
              >
                <SelectTrigger className="bg-white border-zinc-200 mt-2 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-200">
                  {[12, 24, 36, 48, 60].map((m) => (
                    <SelectItem key={m} value={String(m)}>
                      {m} months
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-5">
              <div>
                <Label className="tx-overline">EULA type</Label>
                <Select
                  value={spec.eula_type}
                  onValueChange={(v) => setSpec({ ...spec, eula_type: v })}
                >
                  <SelectTrigger className="bg-white border-zinc-200 mt-2 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-zinc-200">
                    <SelectItem value="standard">Standard AWS EULA</SelectItem>
                    <SelectItem value="custom">Custom / negotiated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="tx-overline">RCMP %</Label>
                <Input
                  type="number"
                  data-testid="rcmp-input"
                  value={spec.rcmp_percent}
                  onChange={(e) => setSpec({ ...spec, rcmp_percent: Number(e.target.value) })}
                  className="mt-2 bg-white border-zinc-200 font-mono rounded-sm"
                />
              </div>
            </div>
            {spec.eula_type === "custom" && (
              <div>
                <Label className="tx-overline">EULA notes / addenda</Label>
                <Textarea
                  value={spec.eula_notes}
                  onChange={(e) => setSpec({ ...spec, eula_notes: e.target.value })}
                  className="mt-2 bg-white border-zinc-200 font-mono text-[12px] min-h-[120px] rounded-sm"
                  placeholder="Data residency, indemnification limits, etc."
                />
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {[
                ["Opportunity", spec.opportunity_id || "—"],
                ["Buyer", `${spec.buyer.company_name} (${spec.buyer.aws_account_id})`],
                ["Reseller", spec.reseller || "—"],
                ["Mode", spec.mode],
                ["Pricing", spec.pricing_model],
                ["TCV", fmtUSD(spec.total_contract_value_usd)],
                ["Duration", `${spec.duration_months} months`],
                ["Currency", spec.currency],
                ["EULA", spec.eula_type],
                ["RCMP", `${spec.rcmp_percent}%`],
              ].map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between border-b border-zinc-200 pb-2">
                  <span className="tx-overline">{k}</span>
                  <span className="font-mono text-[13px] text-zinc-900">{v}</span>
                </div>
              ))}
            </div>

            {validation && (
              <div
                className={`border p-4 rounded-sm ${
                  validation.validation_issues.length === 0
                    ? "border-emerald-500/40 bg-emerald-500/5"
                    : "border-rose-500/40 bg-rose-500/5"
                }`}
              >
                <div className="tx-overline mb-2">
                  Validation · {validation.status}
                </div>
                {validation.validation_issues.length === 0 ? (
                  <div className="text-emerald-700 text-[13px]">All checks passed. Safe to publish.</div>
                ) : (
                  <ul className="list-disc list-inside text-[13px] text-rose-600 space-y-1">
                    {validation.validation_issues.map((i) => (
                      <li key={i}>{i}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {publishedAt && (
              <div className="border border-emerald-500/40 bg-emerald-500/5 p-4">
                <div className="flex items-center gap-2 text-emerald-700 text-[13px]">
                  <Check className="w-4 h-4" /> Published to AMMP at {publishedAt}
                </div>
              </div>
            )}

            <div className="border border-orange-500/50 bg-orange-50 p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div className="text-[12px] text-zinc-700">
                <b className="text-orange-700">Irreversible action.</b> Publishing
                locks pricing, currency, duration and dimensions in AWS AMMP.
                Post-publish changes require deactivation and reissuance.
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-200">
          <div className="flex items-center gap-3">
            {savedId && <StateBadge state={validation?.status || "draft"} />}
            {savedId && (
              <span className="font-mono text-[11px] text-zinc-500">{savedId}</span>
            )}
          </div>
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="border-zinc-300 hover:bg-zinc-100 rounded-sm"
              >
                Back
              </Button>
            )}
            {step < STEPS.length - 1 && (
              <Button
                data-testid="next-step-button"
                onClick={() => setStep(step + 1)}
                className="bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm"
              >
                Next
              </Button>
            )}
            {step === STEPS.length - 1 && !publishedAt && (
              <>
                <Button
                  onClick={validate}
                  data-testid="validate-button"
                  variant="outline"
                  className="border-zinc-300 hover:bg-zinc-100 rounded-sm gap-2"
                >
                  <FileSignature className="w-4 h-4" />
                  Save & Validate
                </Button>
                <Button
                  onClick={() => setPublishOpen(true)}
                  data-testid="open-publish-dialog"
                  disabled={!validation || validation.validation_issues.length > 0}
                  className="bg-orange-600 hover:bg-orange-500 text-white disabled:opacity-40 rounded-sm"
                >
                  Publish authorization
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="bg-white border border-orange-500/40 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Irreversible: publish to AMMP
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              Type <span className="font-mono text-orange-600">PUBLISH</span> to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="designer-publish-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="PUBLISH"
            className="bg-zinc-50 border-zinc-200 font-mono focus-visible:ring-orange-500/40"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPublishOpen(false)}
              className="border-zinc-300 rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={publish}
              disabled={confirm.trim() !== "PUBLISH"}
              className="bg-orange-600 hover:bg-orange-500 text-white disabled:opacity-40 rounded-sm"
            >
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
